import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { RestResponse } from '../models/restResponse.mode';
import { ServiceOrder } from '../models/service-order.model';
import { ChecklistTemplate, ActiveTemplateResponse } from '../models/checklist-template.model';
import { ServiceOrderChecklistItem } from '../models/service-order-checklist.model';
import { CustomFieldDefinition, CustomFieldValue } from '../models/custom-field.model';

export interface ServiceOrderConfig {
  enabledChecklistTemplateId: number | null; // Apenas UM template por empresa
  enabledCustomFieldCategories: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ServiceOrderService {
  private readonly baseUrl = `${environment.base_url}/service-orders`;
  private readonly companyBaseUrl = `${environment.base_url}/companies`;

  constructor(private http: HttpClient) {}

  getAll(search: string, initialDate: Date, finalDate: Date, status: string | null, page: number): Observable<RestResponse<ServiceOrder[]>> {
    const searchTerm = search || '';
    
    // Ajustar Data Inicial para 00:00:00
    const start = new Date(initialDate);
    start.setHours(0, 0, 0, 0);

    // Ajustar Data Final para 23:59:59
    const end = new Date(finalDate);
    end.setHours(23, 59, 59, 999);

    let url = `${this.baseUrl}/list?search=${searchTerm}&initialDate=${start.toISOString()}&finalDate=${end.toISOString()}&page=${page}`;

    if (status !== null && status !== '') {
      url += `&status=${status}`;
    }

    return this.http.get<RestResponse<ServiceOrder[]>>(url);
  }

  getById(id: number): Observable<RestResponse<ServiceOrder>> {
    return this.http.get<RestResponse<ServiceOrder>>(`${this.baseUrl}/find/${id}`);
  }

  create(serviceOrder: ServiceOrder): Observable<RestResponse<ServiceOrder>> {
    return this.http.post<RestResponse<ServiceOrder>>(`${this.baseUrl}/save`, serviceOrder);
  }

  update(id: number, serviceOrder: ServiceOrder): Observable<RestResponse<any>> {
    return this.http.put<RestResponse<any>>(`${this.baseUrl}/update/${id}`, serviceOrder);
  }

  cancel(id: number): Observable<RestResponse<ServiceOrder>> {
    return this.http.patch<RestResponse<ServiceOrder>>(`${this.baseUrl}/cancel/${id}`, null);
  }

  // Checklist Methods
  getChecklistTemplate(companyId: number): Observable<RestResponse<ChecklistTemplate>> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.get<RestResponse<ChecklistTemplate>>(`${this.baseUrl}/checklist-template`, { params });
  }

  // Novo método para buscar template ativo com customFieldDefinitions incluídos
  getActiveTemplate(companyId: number): Observable<RestResponse<ActiveTemplateResponse>> {
    const params = new HttpParams().set('companyId', companyId.toString());
    const url = `${this.baseUrl}/templates/active`;
    console.log('getActiveTemplate - URL:', url, 'companyId:', companyId, 'full URL:', `${url}?companyId=${companyId}`);
    return this.http.get<RestResponse<ActiveTemplateResponse>>(url, { params });
  }

  getChecklistTemplates(companyId?: number): Observable<RestResponse<ChecklistTemplate[]>> {
    let params = new HttpParams();
    if (companyId) {
      params = params.set('companyId', companyId.toString());
    }
    return this.http.get<RestResponse<ChecklistTemplate[]>>(`${this.baseUrl}/checklist-templates`, { params });
  }

  getChecklistItems(serviceOrderId: number): Observable<RestResponse<ServiceOrderChecklistItem[]>> {
    return this.http.get<RestResponse<ServiceOrderChecklistItem[]>>(`${this.baseUrl}/${serviceOrderId}/checklist`);
  }

  saveChecklistItems(serviceOrderId: number, items: ServiceOrderChecklistItem[]): Observable<RestResponse<any>> {
    return this.http.post<RestResponse<any>>(`${this.baseUrl}/${serviceOrderId}/checklist`, items);
  }

  // Custom Fields Methods
  getCustomFieldDefinitions(category?: string, companyId?: number): Observable<RestResponse<CustomFieldDefinition[]>> {
    let params = new HttpParams();
    if (category) {
      params = params.set('category', category);
    }
    if (companyId) {
      params = params.set('companyId', companyId.toString());
    }
    return this.http.get<RestResponse<CustomFieldDefinition[]>>(`${this.baseUrl}/custom-fields/definitions`, { params });
  }

  getCustomFieldValues(serviceOrderId: number): Observable<RestResponse<CustomFieldValue[]>> {
    return this.http.get<RestResponse<CustomFieldValue[]>>(`${this.baseUrl}/${serviceOrderId}/custom-fields`);
  }

  saveCustomFieldValues(serviceOrderId: number, values: Array<{ customFieldDefinitionId: number; value: string }>): Observable<RestResponse<any>> {
    // Backend espera array direto no body, não objeto com propriedade "values"
    // Apenas campos preenchidos devem ser enviados (filtrados no componente)
    return this.http.post<RestResponse<any>>(`${this.baseUrl}/${serviceOrderId}/custom-fields`, values);
  }

  // Company Config Methods
  getServiceOrderConfig(companyId: number): Observable<RestResponse<ServiceOrderConfig>> {
    return this.http.get<RestResponse<ServiceOrderConfig>>(`${this.companyBaseUrl}/service-order-config/${companyId}`);
  }

  updateServiceOrderConfig(companyId: number, config: ServiceOrderConfig): Observable<RestResponse<any>> {
    return this.http.put<RestResponse<any>>(`${this.companyBaseUrl}/service-order-config/${companyId}`, config);
  }
}
